import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import {
  orcamentos as api,
  type ConferenciaDePlanilha,
  type FormatoDePlanilha,
  type VersaoDeTabela,
} from '../api/orcamentos'
import { Aviso } from '../componentes/Aviso'
import { Botao } from '../componentes/Botao'
import { Campo } from '../componentes/Campo'
import { Cartao } from '../componentes/Cartao'
import { Estado } from '../componentes/Estado'
import { useSessao } from '../auth/useSessao'
import { formatar } from '../i18n'
import { mensagemDeErro } from './mensagemDeErro'
import './usuarios.css'
import './tabelaDePrecos.css'

/**
 * Carga da tabela de preços a partir da planilha.
 *
 * A tabela nasce e vive em Excel: o Vicente ajusta lá, não em JSON. Pedir a
 * conversão manual a cada revisão é pedir que a revisão não aconteça — e uma
 * tabela desatualizada é pior que uma ausente, porque ninguém desconfia dela.
 *
 * 🔴 **Conferir e carregar são passos separados.** Ver o que a planilha diz sem
 * criar versão é o que permite corrigir o arquivo antes, em vez de deixar um
 * rastro de versões descartadas no histórico.
 */
export function TabelaDePrecos() {
  const { t } = useTranslation()
  const { pode } = useSessao()

  const [versoes, setVersoes] = useState<VersaoDeTabela[] | null>(null)
  const [falhou, setFalhou] = useState(false)

  const carregar = useCallback(async () => {
    setFalhou(false)
    try {
      setVersoes(await api.versoes())
    } catch {
      setFalhou(true)
    }
  }, [])

  useEffect(() => {
    void carregar()
  }, [carregar])

  if (falhou)
    return <Estado tipo="erro" acao={<Botao onClick={carregar}>{t('comum.tentarDeNovo')}</Botao>} />

  if (!versoes) return <Estado tipo="carregando" />

  return (
    <main className="usuarios">
      <header className="usuarios__cabecalho">
        <div>
          <h1 className="usuarios__titulo">{t('tabela.titulo')}</h1>
          <p className="usuarios__descricao">{t('tabela.descricao')}</p>
        </div>
      </header>

      {pode('ParametroVer') && <Carga podeCarregar={pode('ParametroCtgf')} aoCarregar={carregar} />}

      {/* 🔴 Uma lista só faria "grade v1" e "offset v1" parecerem duas cargas
          da mesma coisa. São três linhas de versão independentes. */}
      {FORMATOS.map((formato) => (
        <LinhaDeVersoes
          key={formato}
          formato={formato}
          versoes={versoes.filter((v) => v.formato === formato)}
        />
      ))}
    </main>
  )
}

const FORMATOS: FormatoDePlanilha[] = ['Grade', 'Livros', 'Offset']

/**
 * O que a planilha traz, no vocabulário do formato dela.
 *
 * "9 famílias, 61 itens, 128 códigos" é o que permite a quem carrega dizer
 * "está certo" sem abrir o Excel ao lado — e é diferente do que faz sentido
 * dizer sobre a calculadora de offset.
 */
function ResumoDoFormato({ conferencia }: { conferencia: ConferenciaDePlanilha }) {
  const { t } = useTranslation()

  const r = conferencia.resumo

  if (!r) return null

  if (conferencia.formato === 'Grade')
    return (
      <>
        <p>
          {t('tabela.resumoDaGrade', {
            familias: r.familias ?? 0,
            itens: r.itens ?? 0,
            variantes: r.variantes ?? 0,
            codigos: r.codigosDeOrcamento ?? 0,
            tolerancias: r.comTolerancia ?? 0,
          })}
        </p>
        {r.nomesDasFamilias && <p className="tabela__lista">{r.nomesDasFamilias.join(' · ')}</p>}
      </>
    )

  if (conferencia.formato === 'Livros')
    return (
      <>
        <p>{t('tabela.resumoDeLivros', { formatos: r.formatos ?? 0, custoFixo: r.custoFixo ?? 0 })}</p>
        {r.nomesDosFormatos && <p className="tabela__lista">{r.nomesDosFormatos.join(' · ')}</p>}
      </>
    )

  return (
    <p>
      {t('tabela.lida', {
        formatos: r.formatos ?? 0,
        miolo: r.papeisDeMiolo ?? 0,
        capa: r.papeisDeCapa ?? 0,
      })}
    </p>
  )
}

function LinhaDeVersoes({
  formato,
  versoes,
}: {
  formato: FormatoDePlanilha
  versoes: VersaoDeTabela[]
}) {
  const { t } = useTranslation()

  return (
    <Cartao titulo={t(`tabela.formato.${formato}`)}>
      <p className="usuarios__descricao">{t(`tabela.formatoAjuda.${formato}`)}</p>

      {versoes.length === 0 ? (
        <Estado tipo="vazio" descricao={t('tabela.semVersaoDoFormato')} />
      ) : (
        <div
          className="usuarios__tabela-rolagem"
          // A região rolável precisa ser alcançável pelo teclado (WCAG 2.1.1):
          // sem isto, a parte escondida da tabela não existe para quem não usa mouse.
          tabIndex={0}
          role="group"
          aria-label={t('tabela.versoesLegendaDe', { formato: t(`tabela.formato.${formato}`) })}
        >
          <table className="usuarios__tabela">
            <caption className="usuarios__legenda">
              {t('tabela.versoesLegendaDe', { formato: t(`tabela.formato.${formato}`) })}
            </caption>
            <thead>
              <tr>
                <th scope="col">{t('tabela.versao')}</th>
                <th scope="col">{t('tabela.vigenteDe')}</th>
                <th scope="col">{t('tabela.carregadaPor')}</th>
                <th scope="col">{t('tabela.arquivo')}</th>
                <th scope="col">{t('tabela.impressao')}</th>
              </tr>
            </thead>
            <tbody>
              {versoes.map((v) => (
                <tr key={v.id}>
                  <td>v{v.numero}</td>
                  {/* 🔴 `data`, não `dataHora`: vigência é um DIA, e o campo que
                      a define é um `type="date"`. Mostrar `00:00` ao lado era o
                      convite para o defeito da 075 — a hora exibida vinha do
                      fuso do servidor, não de uma escolha de alguém.

                      ⚠️ Trocar só isto teria ESCONDIDO o defeito: com o instante
                      errado, a data sozinha mostraria o dia anterior, sem o
                      `21:00` que denunciava a causa. O instante foi corrigido
                      primeiro (`FusoDaOperacao.InicioDoDia`). */}
                  <td>{formatar.data(v.vigenteDe)}</td>
                  <td>{v.carregadaPor}</td>
                  <td>{v.nomeDoArquivo ?? '—'}</td>

                  {/* A impressão distingue duas cargas idênticas de duas
                      cargas diferentes — que é a pergunta ao investigar um
                      preço que mudou sem ninguém ter mexido. */}
                  <td className="tabela__impressao">{v.impressao ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Cartao>
  )
}

function Carga({
  podeCarregar,
  aoCarregar,
}: {
  podeCarregar: boolean
  aoCarregar: () => Promise<void>
}) {
  const { t } = useTranslation()

  const entrada = useRef<HTMLInputElement>(null)
  const [conferencia, setConferencia] = useState<ConferenciaDePlanilha | null>(null)
  const [observacao, setObservacao] = useState('')
  const [vigenteDe, setVigenteDe] = useState('')
  const [ocupado, setOcupado] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [recado, setRecado] = useState<string | null>(null)

  function arquivo() {
    return entrada.current?.files?.[0] ?? null
  }

  async function conferir(evento: FormEvent) {
    evento.preventDefault()

    const escolhido = arquivo()
    if (!escolhido) return

    setErro(null)
    setRecado(null)
    setOcupado(true)

    try {
      setConferencia(await api.conferirPlanilha(escolhido))
    } catch (e) {
      setErro(mensagemDeErro(e, t))
      setConferencia(null)
    } finally {
      setOcupado(false)
    }
  }

  async function carregar() {
    const escolhido = arquivo()
    if (!escolhido) return

    setErro(null)
    setOcupado(true)

    try {
      const resultado = await api.carregarPlanilha(escolhido, observacao, vigenteDe)

      // O recado nomeia o formato: "v1" sozinho não diz em qual das três linhas
      // a carga entrou, e é justamente essa a dúvida.
      setRecado(t('tabela.carregada', {
        numero: resultado.numero,
        formato: resultado.formatoLegivel,
      }))
      setConferencia(null)

      if (entrada.current) entrada.current.value = ''

      await aoCarregar()
    } catch (e) {
      setErro(mensagemDeErro(e, t))
    } finally {
      setOcupado(false)
    }
  }

  return (
    <Cartao titulo={t('tabela.carregar')}>
      <form onSubmit={conferir} noValidate>
        <label className="usuarios__filtro">
          <span>{t('tabela.planilha')}</span>
          <input
            ref={entrada}
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            required
            onChange={() => setConferencia(null)}
          />
        </label>

        <p className="usuarios__descricao">{t('tabela.planilhaAjuda')}</p>

        <Botao type="submit" carregando={ocupado}>
          {t('tabela.conferir')}
        </Botao>
      </form>

      {erro && <Aviso tom="perigo">{erro}</Aviso>}
      {recado && <Aviso tom="sucesso">{recado}</Aviso>}

      {conferencia && !conferencia.sucesso && (
        <div className="tabela__erros">
          {/* A carga inteira é recusada. Aceitar o que deu e completar com os
              valores antigos produziria uma versão que não corresponde a
              planilha nenhuma — e ninguém saberia disso depois. */}
          <Aviso tom="perigo">
            {conferencia.formatoLegivel
              ? t('tabela.recusadaNoFormato', { formato: conferencia.formatoLegivel })
              : t('tabela.recusada')}
          </Aviso>

          {/* A recusa mostra as abas que vieram: sem isso, quem carregou conclui
              que subiu o arquivo certo com um defeito — quando subiu outro
              arquivo. Foi a mensagem que originou esta demanda. */}
          {conferencia.abasEncontradas.length > 0 && (
            <p className="usuarios__descricao">
              {t('tabela.abasEncontradas', { abas: conferencia.abasEncontradas.join(', ') })}
            </p>
          )}

          <ul>
            {conferencia.erros?.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        </div>
      )}

      {conferencia?.sucesso && (
        <div className="tabela__conferencia">
          {/* 🔴 A tela diz QUAL formato identificou, antes de confirmar.
              Reconhecer errado e gravar na linha trocada é o risco de cabeça
              desta carga: não dá erro nenhum, dá preço errado depois. */}
          <Aviso tom="sucesso" titulo={t('tabela.reconhecida', { formato: conferencia.formatoLegivel })}>
            <ResumoDoFormato conferencia={conferencia} />
          </Aviso>

          {(conferencia.achados?.length ?? 0) > 0 && (
            <details className="tabela__achados">
              <summary>
                {t('tabela.achados', { total: conferencia.achados!.length })}
              </summary>
              <ul>
                {conferencia.achados!.map((a) => (
                  <li key={`${a.onde}-${a.mensagem}`}>
                    {/* O sinal em texto, não só a cor (`UI-09`). */}
                    <strong>{a.bloqueia ? t('tabela.bloqueia') : t('tabela.soAviso')}</strong>{' '}
                    {a.mensagem}
                  </li>
                ))}
              </ul>
            </details>
          )}

          {conferencia.avisos.length > 0 && (
            <Aviso tom="alerta">
              <ul>
                {conferencia.avisos.map((a) => (
                  <li key={a}>{a}</li>
                ))}
              </ul>
            </Aviso>
          )}

          {conferencia.mudancas && (
            <>
              {/* 🔴 O achado mais perigoso: formato que some não gera erro
                  nenhum na carga, e o orçamento daquele formato passa a falhar
                  depois, sem ninguém ligar uma coisa à outra. */}
              {conferencia.mudancas.formatosSumidos.length > 0 && (
                <Aviso tom="perigo">
                  {t('tabela.formatosSumidos', {
                    formatos: conferencia.mudancas.formatosSumidos.join(', '),
                  })}
                </Aviso>
              )}

              {conferencia.mudancas.formatosNovos.length > 0 && (
                <Aviso tom="info">
                  {t('tabela.formatosNovos', {
                    formatos: conferencia.mudancas.formatosNovos.join(', '),
                  })}
                </Aviso>
              )}

              {conferencia.mudancas.campos.length > 0 ? (
                <div
                    className="usuarios__tabela-rolagem"
                    // A região rolável precisa ser alcançável pelo teclado (WCAG 2.1.1):
                    // sem isto, a parte escondida da tabela não existe para quem não usa mouse.
                    tabIndex={0}
                    role="group"
                    aria-label={t('tabela.mudancasLegenda')}
                  >
                    <table className="usuarios__tabela">
                    <caption className="usuarios__legenda">{t('tabela.mudancasLegenda')}</caption>
                    <thead>
                      <tr>
                        <th scope="col">{t('tabela.campo')}</th>
                        <th scope="col">{t('tabela.de')}</th>
                        <th scope="col">{t('tabela.para')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {conferencia.mudancas.campos.map((m) => (
                        <tr key={m.campo}>
                          <th scope="row">{m.campo}</th>
                          <td>{formatar.numero(m.de, { maximumFractionDigits: 4 })}</td>
                          <td>
                            <strong>{formatar.numero(m.para, { maximumFractionDigits: 4 })}</strong>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <Aviso tom="info">{t('tabela.semMudancaDeCusto')}</Aviso>
              )}
            </>
          )}

          {podeCarregar ? (
            <>
              <Campo
                rotulo={t('tabela.observacao')}
                value={observacao}
                onChange={(e) => setObservacao(e.target.value)}
                ajuda={t('tabela.observacaoAjuda')}
              />

              <Campo
                rotulo={t('tabela.vigenteDe')}
                type="date"
                value={vigenteDe}
                onChange={(e) => setVigenteDe(e.target.value)}
                ajuda={t('tabela.vigenteDeAjuda')}
              />

              <Botao variante="primario" onClick={carregar} carregando={ocupado}>
                {t('tabela.criarVersao')}
              </Botao>
            </>
          ) : (
            <Aviso tom="info">{t('tabela.somenteCtgf')}</Aviso>
          )}
        </div>
      )}
    </Cartao>
  )
}
